import type { ListingsApi } from '@seek/sbiz-shared-types';
import {
  Box,
  ButtonIcon,
  Card,
  Heading,
  IconDate,
  IconMail,
  IconPhone,
  Inline,
  Stack,
  Text,
  TextLink,
  TextLinkButton,
} from 'braid-design-system';
import { DateTime } from 'luxon';
import React, { useEffect, useRef, useState } from 'react';

import * as styles from './Enquiry.css';

interface Props {
  enquiry: ListingsApi.ListingEnquiry;
}

const Enquiry = ({ enquiry }: Props) => {
  const [shouldTruncateText, setShouldTruncateText] = useState(false);
  const [showFullMessage, setShowFullMessage] = useState(false);

  const messageTextRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (messageTextRef && messageTextRef.current) {
      setShouldTruncateText(
        messageTextRef.current.offsetHeight <
          messageTextRef.current.scrollHeight ||
          messageTextRef.current.offsetWidth <
            messageTextRef.current.scrollWidth,
      );
    }
  }, []);

  const hasPhoneNumber = enquiry.phoneNumber && enquiry.phoneCountryCode;

  const emailHref = `mailto:${enquiry.emailAddress}?subject=Regarding your enquiry on SEEK Business`;
  const phoneHref = `tel:+${enquiry.phoneCountryCode}${enquiry.phoneNumber}`;

  return (
    <Card rounded tone="formAccent" component="section">
      <Box paddingRight="large">
        <Stack space="large">
          <Heading level="4">{enquiry.name}</Heading>
          <Stack space="small">
            <Text tone="secondary">
              <div
                className={!showFullMessage ? styles.lineClamp : undefined}
                ref={messageTextRef}
              >
                {enquiry.message}
              </div>
            </Text>
            {shouldTruncateText && (
              <Text>
                <TextLinkButton
                  onClick={() => setShowFullMessage(!showFullMessage)}
                >
                  {showFullMessage ? 'Show Less' : 'Show More...'}
                </TextLinkButton>
              </Text>
            )}
          </Stack>

          <Stack space="gutter">
            <Inline space="gutter" alignY="center">
              <ButtonIcon
                variant="soft"
                icon={<IconPhone />}
                label={hasPhoneNumber ? 'Call phone number' : 'Phone number'}
                id="phone-number-icon"
                onClick={() => {
                  if (hasPhoneNumber) {
                    window.location.href = phoneHref;
                  }
                }}
              />
              <Text tone="secondary" size="standard">
                {hasPhoneNumber ? (
                  <TextLink href={phoneHref} hitArea="large">
                    {`+${enquiry.phoneCountryCode} ${enquiry.phoneNumber}`}
                  </TextLink>
                ) : (
                  'Not Provided'
                )}
              </Text>
            </Inline>
            <Inline space="gutter" alignY="center">
              <ButtonIcon
                variant="soft"
                icon={<IconMail />}
                label="Send email"
                id="email-icon"
                onClick={() => {
                  window.location.href = emailHref;
                }}
              />
              <Text tone="secondary" size="standard">
                <TextLink href={emailHref} hitArea="large">
                  {enquiry.emailAddress}
                </TextLink>
              </Text>
            </Inline>
            <Inline space="gutter" alignY="center">
              <ButtonIcon
                variant="soft"
                icon={<IconDate />}
                label="Received Date"
                id="received-date-icon"
              />
              <Text tone="secondary" size="standard">
                {DateTime.fromISO(enquiry.lastUpdated).toFormat('MMM yyyy')}
              </Text>
            </Inline>
          </Stack>
        </Stack>
      </Box>
    </Card>
  );
};

export default Enquiry;
